import { useEffect, useState, useRef, useCallback } from "react";
import { useField } from "formik";
import "../../../admin/TeacherAdmin/classroom/SelectStudents.css";
import SelectStudentTable from "../../TeacherAdmin/classroom/SelectStudentTable";
import StudentSelectorSkeleton from "../../TeacherAdmin/classroom/StudentSelectorSkeleton";
import { useGetStudents } from "../../../../hooks/school/student/useGetStudents";
import { useGetTeachers } from "../../../../hooks/school/teacher/useGetTeachers";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";
import { useDebounce } from "../../../../utils/useDebounce";
import TableError from "../../common/TableError";
import "./SchoolAdminCreateClassroom.css";

/**
 * @typedef {"searching" | "infinite-scroll" | "initial-load" | "none"} LoadingType
 */

/**
 * SchoolAdminStudentSelector Component
 * A student selector for School Admin that includes a teacher dropdown
 * with search + infinite scroll, followed by the student dual-table selector.
 *
 * @param {Object} props
 * @param {string} props.name - Formik field name for selected students
 * @param {string} props.teacherFieldName - Formik field name for selected teacher
 * @param {boolean} [props.refresh=false] - Toggle to trigger a refresh
 * @param {Object} [props.initialTeacher=null] - Pre-selected teacher (for edit mode)
 * @param {boolean} [props.readOnlyTeacher=false] - If true, teacher cannot be changed
 * @param {boolean} [props.hideTeacherSection=false] - If true, hides the teacher selector entirely (for update mode where teacher display is handled by the parent)
 * @param {Object} [props.initialSelectedStudents=null] - Pre-selected students (for edit mode)
 */
const SchoolAdminStudentSelector = ({
  name,
  teacherFieldName,
  refresh = false,
  initialTeacher = null,
  readOnlyTeacher = false,
  hideTeacherSection = false,
  initialSelectedStudents = null,
}) => {
  const [field, meta, helpers] = useField(name);
  const [, , teacherHelpers] = useField(teacherFieldName);

  // ─── Teacher Dropdown State ───
  const {
    loading: teacherLoading,
    teachers,
    getTeachers,
    metaData: teacherMeta,
  } = useGetTeachers();

  const [teacherSearch, setTeacherSearch] = useState("");
  const debouncedTeacherSearch = useDebounce(teacherSearch, 400);
  const [teacherDropdownOpen, setTeacherDropdownOpen] = useState(false);
  const [selectedTeacher, setSelectedTeacher] = useState(initialTeacher);
  const [teacherPage, setTeacherPage] = useState(1);
  const [teacherHasInit, setTeacherHasInit] = useState(false);
  const teacherDropdownRef = useRef(null);
  const teacherObserverTarget = useRef(null);

  const {
    students,
    loading: studentLoading,
    error: studentError,
    meta: studentPaginationMeta,
    getStudents,
  } = useGetStudents();

  const [studentSearch, setStudentSearch] = useState("");
  const debouncedStudentSearch = useDebounce(studentSearch, 400);

  const [currentPage, setCurrentPage] = useState(1);
  const [allStudents, setAllStudents] = useState([]);
  const [hasMorePages, setHasMorePages] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [hasStudentInit, setHasStudentInit] = useState(false);

  const [checkedStudents, setCheckedStudents] = useState([]);
  const observerTarget = useRef(null);
  /** @type {React.MutableRefObject<LoadingType>} */
  const loadingRef = useRef("none");

  // ═══════════════════════════════════════════════════
  // TEACHER DROPDOWN LOGIC
  // ═══════════════════════════════════════════════════

  /** Initial teacher fetch (skip if teacher is read-only in edit mode) */
  useEffect(() => {
    if (!teacherHasInit && !readOnlyTeacher) {
      getTeachers(1, ITEMS_PER_PAGE, false, "");
      setTeacherHasInit(true);
    }
  }, [teacherHasInit, readOnlyTeacher]);

  /** Pre-select teacher from initialTeacher prop (edit mode) */
  useEffect(() => {
    if (initialTeacher && !selectedTeacher) {
      setSelectedTeacher(initialTeacher);
      teacherHelpers.setValue(initialTeacher);
    }
  }, [initialTeacher]);

  /** Re-fetch teachers when search changes */
  useEffect(() => {
    if (!teacherHasInit) return;
    setTeacherPage(1);
    getTeachers(1, ITEMS_PER_PAGE, false, debouncedTeacherSearch);
  }, [debouncedTeacherSearch]);

  /** Infinite scroll for teacher dropdown */
  useEffect(() => {
    const target = teacherObserverTarget.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          !teacherLoading &&
          teacherMeta &&
          teacherMeta.current_page < teacherMeta.last_page
        ) {
          const nextPage = teacherPage + 1;
          setTeacherPage(nextPage);
          getTeachers(nextPage, ITEMS_PER_PAGE, true, debouncedTeacherSearch);
        }
      },
      { threshold: 0.1, rootMargin: "50px" },
    );

    observer.observe(target);
    return () => observer.unobserve(target);
  }, [teacherLoading, teacherMeta, teacherPage, debouncedTeacherSearch]);

  /** Close dropdown on outside click */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        teacherDropdownRef.current &&
        !teacherDropdownRef.current.contains(e.target)
      ) {
        setTeacherDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /** Select a teacher */
  const handleSelectTeacher = (teacher) => {
    setSelectedTeacher(teacher);
    teacherHelpers.setValue(teacher);
    setTeacherDropdownOpen(false);
    setTeacherSearch("");

    // Reset student state for new teacher
    setAllStudents([]);
    setCurrentPage(1);
    setHasMorePages(true);
    setCheckedStudents([]);
    helpers.setValue([]);
    setIsInitialLoad(true);
    setHasStudentInit(false);
    setStudentSearch("");
  };

  /** Clear selected teacher */
  const handleClearTeacher = () => {
    setSelectedTeacher(null);
    teacherHelpers.setValue(null);
    setAllStudents([]);
    setCurrentPage(1);
    setHasMorePages(true);
    setCheckedStudents([]);
    helpers.setValue([]);
    setIsInitialLoad(true);
    setHasStudentInit(false);
    setStudentSearch("");
  };

  const teacherHasMore =
    teacherMeta && teacherMeta.current_page < teacherMeta.last_page;

  // ═══════════════════════════════════════════════════
  // STUDENT SELECTOR LOGIC (mirrors StudentSelector.jsx)
  // ═══════════════════════════════════════════════════

  /** @type {import("../../TeacherAdmin/classroom/StudentSelector").FetchStudentsCallback} */
  const fetchStudents = useCallback(
    async (pageNumber, isNewSearch = false, loadingType) => {
      if (loadingRef.current !== "none" || !selectedTeacher) return;
      loadingRef.current = loadingType;

      try {
        const filters = {};
        if (debouncedStudentSearch.trim()) {
          filters.name = debouncedStudentSearch.trim();
        }
        filters.status = 1;

        await getStudents({
          page: pageNumber,
          per_page: ITEMS_PER_PAGE,
          filters,
          teacher_id: selectedTeacher.id,
          unassigned_students: true,
        });

        setCurrentPage(pageNumber);
        if (isNewSearch) setCheckedStudents([]);
        if (isInitialLoad) setIsInitialLoad(false);
      } catch (err) {
      } finally {
        loadingRef.current = "none";
        setIsLoadingMore(false);
      }
    },
    [debouncedStudentSearch, getStudents, isInitialLoad, selectedTeacher],
  );

  /** Initial student fetch when teacher selected */
  useEffect(() => {
    if (selectedTeacher && !hasStudentInit) {
      fetchStudents(1, true, "initial-load");
      setHasStudentInit(true);
    }
  }, [selectedTeacher, hasStudentInit, fetchStudents]);

  /** Initialize selected students from initialSelectedStudents (edit mode) */
  useEffect(() => {
    if (
      initialSelectedStudents &&
      initialSelectedStudents?.data?.length > 0 &&
      !field.value?.length
    ) {
      const selectedWithFullData = initialSelectedStudents.data.map((student) => ({
        id: student.id,
        username: student.username,
        name: student.name,
        student_code: student.student_code,
      }));
      helpers.setValue(selectedWithFullData);
    }
  }, [initialSelectedStudents]);

  /** Refresh trigger from parent — reset everything */
  useEffect(() => {
    if (hasStudentInit || selectedTeacher) {
      if (!readOnlyTeacher) {
        setSelectedTeacher(null);
        teacherHelpers.setValue(null);
      }
      setAllStudents([]);
      setCurrentPage(1);
      setHasMorePages(true);
      setCheckedStudents([]);
      if (!readOnlyTeacher) {
        helpers.setValue([]);
      }
      setIsInitialLoad(true);
      setHasStudentInit(false);
      setStudentSearch("");
    }
  }, [refresh]);

  /** Handle debounced student search changes */
  useEffect(() => {
    if (!hasStudentInit || isInitialLoad || !selectedTeacher) return;
    setCurrentPage(1);
    setAllStudents([]);
    setHasMorePages(true);
    fetchStudents(1, true, "searching");
  }, [debouncedStudentSearch]);

  /** Update student list when API responds */
  useEffect(() => {
    if (students) {
      if (currentPage === 1) {
        setAllStudents(students);
      } else {
        setAllStudents((prev) => {
          const existingIds = new Set(prev.map((s) => s.id));
          const newStudents = students.filter((s) => !existingIds.has(s.id));
          return [...prev, ...newStudents];
        });
      }
      setHasMorePages(
        studentPaginationMeta
          ? studentPaginationMeta.current_page < studentPaginationMeta.last_page
          : false,
      );
    }
  }, [students, currentPage, studentPaginationMeta]);

  /** Load more students for infinite scroll */
  const loadMore = useCallback(() => {
    if (loadingRef.current === "none" && hasMorePages && !isLoadingMore) {
      setIsLoadingMore(true);
      const nextPage = currentPage + 1;
      fetchStudents(nextPage, false, "infinite-scroll");
    }
  }, [currentPage, hasMorePages, isLoadingMore, fetchStudents]);

  /** Intersection Observer for student infinite scroll */
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          hasMorePages &&
          loadingRef.current === "none" &&
          !isLoadingMore
        ) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: "100px" },
    );

    const currentTarget = observerTarget.current;
    if (currentTarget) observer.observe(currentTarget);
    return () => {
      if (currentTarget) observer.unobserve(currentTarget);
    };
  }, [hasMorePages, isLoadingMore, loadMore]);

  // ─── Student selection helpers ───
  const getSelectedStudentIds = () => (field.value || []).map((s) => s.id);

  const toggleStudentCheck = (studentId) => {
    setCheckedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  const toggleSelectAll = () => {
    const available = getAvailableStudents();
    const availableIds = available.map((s) => s.id);
    const allChecked = availableIds.every((id) => checkedStudents.includes(id));

    if (allChecked) {
      setCheckedStudents((prev) =>
        prev.filter((id) => !availableIds.includes(id)),
      );
    } else {
      setCheckedStudents((prev) => {
        const newChecked = [...prev];
        availableIds.forEach((id) => {
          if (!newChecked.includes(id)) newChecked.push(id);
        });
        return newChecked;
      });
    }
  };

  const addStudents = () => {
    if (checkedStudents.length === 0) return;
    const studentsToAdd = allStudents.filter((s) =>
      checkedStudents.includes(s.id),
    );

    // Also include students from initialSelectedStudents (edit mode)
    if (initialSelectedStudents && initialSelectedStudents?.data?.length > 0) {
      initialSelectedStudents.data.forEach((s) => {
        if (checkedStudents.includes(s.id)) {
          studentsToAdd.push(s);
        }
      });
    }

    const currentSelected = field.value || [];
    const existingIds = new Set(currentSelected.map((s) => s.id));
    const newStudents = studentsToAdd
      .filter((s) => !existingIds.has(s.id))
      .map((s) => ({
        id: s.id,
        username: s.username,
        name: s.name,
        student_code: s.student_code,
      }));

    if (newStudents.length > 0) {
      helpers.setValue([...currentSelected, ...newStudents]);
    }
    setCheckedStudents([]);
    loadMore();
  };

  const removeStudent = (studentId) => {
    helpers.setValue(field.value.filter((s) => s.id !== studentId));
    setCheckedStudents((prev) => prev.filter((id) => id !== studentId));
  };

  const getAvailableStudents = () => {
    if (!allStudents) return [];
    const selectedIds = new Set(getSelectedStudentIds());
    let available = allStudents.filter((s) => !selectedIds.has(s.id));

    // Include students from initialSelectedStudents that were removed (edit mode)
    if (initialSelectedStudents && initialSelectedStudents?.data?.length > 0) {
      available = [
        ...available,
        ...initialSelectedStudents.data.filter(
          (s) => !selectedIds.has(s.id),
        ),
      ];
    }

    return available;
  };

  const getSelectedStudentsForDisplay = () =>
    (field.value || []).map((s) => ({
      id: s.id,
      username: s.username,
      name: s.name,
      student_code: s.student_code,
    }));

  // ─── Render ───
  const availableStudents = getAvailableStudents();
  const selectedStudents = getSelectedStudentsForDisplay();

  return (
    <div>
      {/* ─── Teacher Dropdown (hidden in update mode; parent shows its own display) ─── */}
      {!hideTeacherSection && (
        <div className="teacher-dropdown-wrapper" ref={teacherDropdownRef}>
          <label className="teacher-dropdown-label">Select Teacher</label>

          {selectedTeacher ? (
            <div className="teacher-selected-badge">
              <span>
                {selectedTeacher.firstName || selectedTeacher.name}{" "}
                {selectedTeacher.lastName || ""}
                {selectedTeacher.email && (
                  <span style={{ color: "#6c757d", marginLeft: 6 }}>
                    ({selectedTeacher.email})
                  </span>
                )}
              </span>
              {!readOnlyTeacher && (
                <button
                  type="button"
                  onClick={handleClearTeacher}
                  title="Change teacher"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              )}
            </div>
          ) : readOnlyTeacher ? (
            <div className="teacher-selected-badge" style={{ backgroundColor: "#f8d7da", color: "#842029", border: "1px solid #f5c6cb" }}>
              <span>
                <i className="bi bi-exclamation-triangle-fill me-2"></i>
                Teacher details not found, maybe teacher has been deleted
              </span>
            </div>
          ) : (
            <div className="teacher-dropdown-input-wrapper">
              <input
                type="text"
                className="teacher-dropdown-input"
                placeholder="Search teacher by name..."
                value={teacherSearch}
                onChange={(e) => {
                  setTeacherSearch(e.target.value);
                  if (!teacherDropdownOpen) setTeacherDropdownOpen(true);
                }}
                onFocus={() => setTeacherDropdownOpen(true)}
                autoComplete="off"
              />
              <i
                className={`bi bi-chevron-down teacher-dropdown-chevron ${
                  teacherDropdownOpen ? "open" : ""
                }`}
              ></i>

              {teacherDropdownOpen && (
                <ul className="teacher-dropdown-list">
                  {teachers && teachers.length > 0 ? (
                    <>
                      {teachers.map((teacher) => (
                        <li
                          key={teacher.id}
                          className="teacher-dropdown-item"
                          onClick={() => handleSelectTeacher(teacher)}
                        >
                          <span className="teacher-dropdown-item-name">
                            {teacher.firstName || teacher.name}{" "}
                            {teacher.lastName || ""}
                          </span>
                          {teacher.email && (
                            <span className="teacher-dropdown-item-email">
                              {teacher.email}
                            </span>
                          )}
                        </li>
                      ))}

                      {/* Infinite scroll sentinel */}
                      {teacherHasMore && (
                        <li
                          ref={teacherObserverTarget}
                          className="teacher-dropdown-loader"
                        >
                          {teacherLoading && (
                            <div
                              className="spinner-border spinner-border-sm"
                              role="status"
                            >
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          )}
                        </li>
                      )}
                    </>
                  ) : teacherLoading ? (
                    <li className="teacher-dropdown-loader">
                      <div
                        className="spinner-border spinner-border-sm"
                        role="status"
                      >
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </li>
                  ) : (
                    <li className="teacher-dropdown-empty">No teachers found</li>
                  )}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {/* ─── Student Section ─── */}
      <div className="mt-4">
        <div className="add-student-classroom">Add Students</div>
      </div>

      {!selectedTeacher ? (
        <div className="student-section-placeholder mt-3">
          <i className="bi bi-person-plus"></i>
          Please select a teacher first to view their students
        </div>
      ) : isInitialLoad && studentLoading ? (
        <div className="mt-3">
          <div className="student-selector-search-wrapper">
            <label className="student-selector-label">Students</label>
            <div className="student-selector-search w-full">
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search by name..."
                disabled
                className="student-selector-search-input"
                style={{ opacity: 0.6 }}
              />
            </div>
          </div>
          <div className="selectStudent-container">
            <div className="select-table-wrapper">
              <StudentSelectorSkeleton rows={5} type="select" />
            </div>
            <div className="select-table-wrapper">
              <StudentSelectorSkeleton rows={3} type="selected" />
            </div>
          </div>
        </div>
      ) : studentError && !allStudents.length ? (
        <TableError
          error={studentError}
          handleRefresh={() => {
            fetchStudents(1, true, "initial-load");
          }}
          loading={studentLoading}
        />
      ) : (
        <div className="mt-3">
          {/* Search Bar */}
          <div className="student-selector-search-wrapper">
            <label className="student-selector-label">Students</label>
            <div className="student-selector-search w-full">
              <i className="bi bi-search"></i>
              <input
                type="text"
                placeholder="Search by name..."
                value={studentSearch}
                onChange={(e) => setStudentSearch(e.target.value)}
                className="student-selector-search-input"
                disabled={studentLoading && !isInitialLoad}
              />
            </div>
            <div>
              {studentLoading && !isInitialLoad && (
                <div role="status">
                  <span>Searching...</span>
                </div>
              )}
            </div>
          </div>

          <div className="selectStudent-container">
            {/* Available Students Table */}
            <div className="select-table-wrapper">
              <SelectStudentTable
                students={availableStudents}
                type="select"
                checkedStudents={checkedStudents}
                toggleStudentCheck={toggleStudentCheck}
                toggleSelectAll={toggleSelectAll}
                addStudents={addStudents}
                observerTarget={observerTarget}
                isLoadingMore={isLoadingMore}
                hasMore={hasMorePages}
                isSearching={studentLoading && !isInitialLoad}
                searchQuery={studentSearch}
              />
            </div>

            {/* Selected Students Table */}
            <div className="select-table-wrapper">
              <SelectStudentTable
                students={selectedStudents}
                type="selected"
                removeStudent={removeStudent}
                selectedCount={selectedStudents.length}
              />
            </div>
          </div>
        </div>
      )}

      {/* Formik Validation Error */}
      {meta.touched && meta.error && (
        <div className="invalid-feedback d-block mt-2">
          <i className="bi bi-exclamation-circle-fill me-1"></i>
          {meta.error}
        </div>
      )}
    </div>
  );
};

export default SchoolAdminStudentSelector;
